import enhancedPptxService from './enhancedPptxService';
import { extractSlidesJsonFromDocumentHtml } from '../utils/presentationEmbed';

/** Optional `<!--ppt_theme:...-->` comment (URI-encoded JSON of the template's stored theme). */
const EMBEDDED_THEME_RE = /<!--ppt_theme:([^>]*)-->/;

/**
 * True for a theme object worth handing to the slide builder.
 * @param {unknown} theme - Candidate.
 * @returns {boolean} Whether it carries colours or fonts.
 */
function isUsableTheme(theme) {
  if (!theme || typeof theme !== 'object' || Array.isArray(theme)) return false;
  return Boolean(theme.colors || theme.colorScheme || theme.clrScheme || theme.fonts || theme.fontScheme || theme.majorFont || theme.minorFont);
}

/**
 * Reads the stored theme a presentation document carries in a `<!--ppt_theme:...-->` comment.
 * @param {string} htmlContent - Stored document HTML.
 * @returns {object|null} Theme ({ colors, fonts }) or null.
 */
export function extractThemeFromDocumentHtml(htmlContent) {
  if (typeof htmlContent !== 'string') return null;
  const match = htmlContent.match(EMBEDDED_THEME_RE);
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1]));
    return isUsableTheme(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The theme saved with a PowerPoint template by the importer (`templates.content.theme`:
 * `{ colors: { dk1, lt1, dk2, lt2, accent1..6, ... }, fonts?: { major, minor } }`).
 * @param {object|null|undefined} template - Template row (content may be a JSON string) or its
 *   parsed content.
 * @returns {object|null} Theme or null.
 */
export function themeFromTemplate(template) {
  if (!template || typeof template !== 'object') return null;
  if (isUsableTheme(template.theme)) return template.theme;
  let content = template.content;
  if (typeof content === 'string') {
    try {
      content = JSON.parse(content);
    } catch {
      return null;
    }
  }
  return content && typeof content === 'object' && isUsableTheme(content.theme) ? content.theme : null;
}

/**
 * The template theme the worker copies onto a generated deck
 * (`generated_documents.trust_metadata.presentation.theme`).
 * @param {object|null|undefined} document - Generated document row (trust_metadata may be a JSON
 *   string).
 * @returns {object|null} Theme or null.
 */
export function themeFromDocument(document) {
  if (!document || typeof document !== 'object') return null;
  let trust = document.trust_metadata ?? document.trustMetadata;
  if (typeof trust === 'string') {
    try {
      trust = JSON.parse(trust);
    } catch {
      return null;
    }
  }
  const theme = trust && typeof trust === 'object' ? trust.presentation?.theme : null;
  return isUsableTheme(theme) ? theme : null;
}

/**
 * Export options with the deck theme resolved: an explicit `options.theme` wins, then the theme
 * of `options.template`, then the theme saved on `options.document` (a generated deck row), then a
 * theme embedded in the document HTML. `template` and `document` themselves are not passed on
 * (they can be large).
 * @param {string} htmlContent - Document HTML.
 * @param {object} options - Options (page setup keys already removed).
 * @returns {object} Options for the slide builder.
 */
export function withDeckTheme(htmlContent, options) {
  const { template, document, ...rest } = options && typeof options === 'object' ? options : {};
  const theme = isUsableTheme(rest.theme)
    ? rest.theme
    : themeFromTemplate(template) || themeFromDocument(document) || extractThemeFromDocumentHtml(htmlContent);
  if (theme) rest.theme = theme;
  else delete rest.theme;
  return rest;
}

/**
 * The stored theme of a deck's template, read on demand (the template list no longer carries
 * `content`). Used when a deck itself carries no theme: decks generated before the worker copied
 * the theme onto the document, so legacy exports still get the company colours and fonts.
 * @param {string|null|undefined} templateId - Template id (generated_documents.template_id).
 * @param {{ client?: object }} [deps] - Supabase client (tests).
 * @returns {Promise<object|null>} Theme or null (no template, no access, no stored theme).
 */
export async function loadTemplateTheme(templateId, { client } = {}) {
  if (!templateId || typeof templateId !== 'string') return null;
  try {
    const supabase = client || (await import('../lib/supabase')).supabase;
    const { data, error } = await supabase.from('templates').select('content').eq('id', templateId).maybeSingle();
    if (error || !data) return null;
    return themeFromTemplate(data);
  } catch {
    return null;
  }
}

/** Matches page break markup (the editor's `pageBreak` node and legacy inline-style breaks). */
const PAGE_BREAK_HINT_RE = /data-page-break|break-(?:after|before)\s*:\s*(?:page|always)/i;
const LEGACY_PAGE_BREAK_STYLE_RE = /(?:page-break-(?:after|before)\s*:\s*always|break-(?:after|before)\s*:\s*page)/i;

/**
 * True when a top-level element only marks a page break.
 * @param {Element} el - DOM element.
 * @returns {boolean} Whether the element is a page break.
 */
function isPageBreakElement(el) {
  const tag = el.tagName.toLowerCase();
  if (tag === 'div' && el.hasAttribute('data-page-break')) {
    return el.getAttribute('data-page-break') !== 'false';
  }
  if (tag !== 'div' && tag !== 'p') return false;
  if (!LEGACY_PAGE_BREAK_STYLE_RE.test(el.getAttribute('style') || '')) return false;
  return !el.textContent.trim() && !el.querySelector('img, table, svg, iframe, video');
}

/**
 * Splits document HTML at top-level page breaks.
 * @param {string} htmlContent - Document HTML.
 * @returns {Element[][]} Top-level elements per page (page breaks removed; may contain empty pages).
 */
export function splitHtmlAtPageBreaks(htmlContent) {
  const doc = new DOMParser().parseFromString(htmlContent || '', 'text/html');
  const pages = [[]];
  Array.from(doc.body.children).forEach((el) => {
    if (isPageBreakElement(el)) {
      pages.push([]);
    } else {
      pages[pages.length - 1].push(el);
    }
  });
  return pages;
}

/**
 * Groups HTML into slides where every page break starts a new slide. Within one page the normal
 * grouping (`enhancedPptxService.convertSectionsToSlides`: new slide on h1 / h2, at most N blocks
 * per slide) still applies. Empty pages (consecutive breaks) produce no slide.
 *
 * @param {string} htmlContent - Document HTML that contains at least one page break.
 * @param {object} [options] - Grouping options (`maxSectionsPerSlide`).
 * @returns {{ title: string, content: string }[]} Slide definitions with HTML content.
 */
export function buildSlidesFromPagedHtml(htmlContent, options = {}) {
  const slides = [];
  splitHtmlAtPageBreaks(htmlContent).forEach((elements) => {
    const blocks = elements
      .map((el) => {
        const tag = el.tagName.toLowerCase();
        const rawText = el.textContent.trim();
        const isHeading = tag === 'h1' || tag === 'h2' || tag === 'h3';
        const hasMedia = ['ul', 'ol', 'table', 'img', 'blockquote'].includes(tag) || Boolean(el.querySelector('img'));
        if (!isHeading && !hasMedia && !rawText) return null;
        // Only `type` and `rawText` drive the grouping; `html` rides along for the slide content.
        return { type: isHeading ? tag : 'block', rawText, html: el.outerHTML };
      })
      .filter(Boolean);
    if (blocks.length === 0) return;
    enhancedPptxService.convertSectionsToSlides(blocks, options).forEach((slide) => {
      slides.push({
        title: slide.title || '',
        content: slide.content.map((block) => block.html).join(''),
      });
    });
  });
  return slides;
}

/** Export options that describe printed pages (Word / PDF / HTML) and mean nothing to slides. */
const PAGE_SETUP_OPTION_KEYS = ['pageSetup', 'page_setup', 'fieldContext', 'paperSize', 'fetchImpl', 'measureImage', 'loadTemplateTheme'];

/**
 * Copy of export options without the page setup keys.
 * @param {object} [options] - Export options.
 * @returns {object} Options for the slide builder.
 */
export function withoutPageSetupOptions(options) {
  const rest = { ...(options && typeof options === 'object' ? options : {}) };
  PAGE_SETUP_OPTION_KEYS.forEach((key) => {
    delete rest[key];
  });
  return rest;
}

/**
 * Single entry point for exporting stored document HTML to a .pptx file (browser download).
 */
class PptxExportService {
  /**
   * Export document HTML to PowerPoint, preferring embedded slide JSON when present. Page breaks
   * in the document start a new slide.
   * @param {string} htmlContent - Stored `generated_documents.content`.
   * @param {string} fileName - Target file name (e.g. `deck.pptx`).
   * @param {string} [_projectName] - Reserved for parity with other export services.
   * @param {object} [rawOptions] - Options passed to {@link enhancedPptxService.exportSlidesToPptx}
   *   (page setup keys removed first, see `withoutPageSetupOptions`). `theme` (the template's
   *   stored theme), `template` (the template row) or `document` (the generated deck row, whose
   *   trust_metadata.presentation.theme the worker fills) gives the deck its colours and fonts;
   *   without any of them, a `<!--ppt_theme:...-->` comment in the HTML is used when present, and
   *   then the stored theme of the document's template (`document.template_id`, read on demand;
   *   `loadTemplateTheme` overrides the reader in tests).
   * @returns {Promise<boolean>} True when export succeeded.
   */
  async exportToPptx(htmlContent, fileName, _projectName = '', rawOptions = {}) {
    // Slides have no page setup: headers, footers, margins, orientation and paper size (and the
    // live-field values for them) are ignored here, never handed to the slide builder.
    let options = withDeckTheme(htmlContent, withoutPageSetupOptions(rawOptions));
    const doc = rawOptions && typeof rawOptions === 'object' ? rawOptions.document : null;
    const templateId = doc && typeof doc === 'object' ? doc.template_id || doc.templateId : null;
    if (!options.theme && !rawOptions?.template && templateId) {
      const loader = typeof rawOptions.loadTemplateTheme === 'function' ? rawOptions.loadTemplateTheme : loadTemplateTheme;
      const theme = await loader(templateId);
      if (isUsableTheme(theme)) options = { ...options, theme };
    }
    const slides = extractSlidesJsonFromDocumentHtml(htmlContent);
    if (slides && Array.isArray(slides) && slides.length > 0) {
      return enhancedPptxService.exportSlidesToPptx(slides, fileName, options);
    }
    if (typeof htmlContent === 'string' && PAGE_BREAK_HINT_RE.test(htmlContent)) {
      let pagedSlides = null;
      try {
        pagedSlides = buildSlidesFromPagedHtml(htmlContent, options);
      } catch (error) {
        console.warn('Page break slide grouping failed; using default grouping.', error);
      }
      if (pagedSlides && pagedSlides.length > 0) {
        return enhancedPptxService.exportSlidesToPptx(pagedSlides, fileName, options);
      }
    }
    return enhancedPptxService.exportHtmlToPptx(htmlContent, fileName, options);
  }
}

const pptxExportService = new PptxExportService();
export default pptxExportService;
